import React, { useMemo } from "react";
import { useTranslation } from "react-i18next";
import { GetReportsBetweenDatesResult } from "../../../types/Rows";
import { PlannedReport } from "../../../api/reportsApi";
import { getPlannedVisitPeople } from "../../../utils/plannedVisitUtils";

interface CalendarFiltersProps {
  reports: GetReportsBetweenDatesResult[];
  plannedReports: PlannedReport[];
  selectedStudy: string;
  selectedEmail: string;
  onStudyChange: (study: string) => void;
  onEmailChange: (email: string) => void;
}

export const CalendarFilters: React.FC<CalendarFiltersProps> = ({
  reports,
  plannedReports,
  selectedStudy,
  selectedEmail,
  onStudyChange,
  onEmailChange,
}) => {
  const { t } = useTranslation();

  const studies = useMemo(() => {
    const unique = Array.from(new Set([
      ...reports.map(r => r.studyName),
      ...plannedReports.map(p => p.studyName),
    ].filter((s): s is string => Boolean(s))));
    const sorted = unique.sort();
    // Keep selected study even if not in current filtered data
    if (selectedStudy && !sorted.includes(selectedStudy)) {
      sorted.push(selectedStudy);
      sorted.sort();
    }
    return sorted;
  }, [reports, plannedReports, selectedStudy]);

  const emails = useMemo(() => {
    const unique = Array.from(new Set([
      ...reports.map(r => r.createdBy),
      ...plannedReports.flatMap(getPlannedVisitPeople),
    ].filter(Boolean)));
    const sorted = unique.sort();
    // Keep selected email even if not in current filtered data
    if (selectedEmail && !sorted.includes(selectedEmail)) {
      sorted.push(selectedEmail);
      sorted.sort();
    }
    return sorted;
  }, [reports, plannedReports, selectedEmail]);

  return (
    <div className="flex flex-wrap gap-3 items-center">
      <div className="flex items-center gap-2">
        <label className="text-[10px] font-semibold uppercase tracking-[0.06em] text-[var(--sc-muted)]">{t("study")}:</label>
        <select
          value={selectedStudy}
          onChange={(e) => onStudyChange(e.target.value)}
          className="rounded-lg border border-[var(--sc-border)] px-3 py-1.5 text-sm text-[var(--sc-text)] bg-[var(--sc-surface)] focus:outline-none focus:ring-1 focus:ring-[var(--sc-amber)]"
        >
          <option value="">{t("all")}</option>
          {studies.map((study) => (
            <option key={study} value={study}>
              {study}
            </option>
          ))}
        </select>
      </div>
      
      <div className="flex items-center gap-2">
        <label className="text-[10px] font-semibold uppercase tracking-[0.06em] text-[var(--sc-muted)]">{t("monitor")}:</label>
        <select
          value={selectedEmail}
          onChange={(e) => onEmailChange(e.target.value)}
          className="rounded-lg border border-[var(--sc-border)] px-3 py-1.5 text-sm text-[var(--sc-text)] bg-[var(--sc-surface)] focus:outline-none focus:ring-1 focus:ring-[var(--sc-amber)]"
        >
          <option value="">{t("all")}</option>
          {emails.map((email) => (
            <option key={email} value={email}>
              {email}
            </option>
          ))}
        </select>
      </div>

      {(selectedStudy || selectedEmail) && (
        <button
          onClick={() => {
            onStudyChange("");
            onEmailChange("");
          }}
          className="text-sm text-[#F4A832] hover:text-[#e09620] font-medium"
        >
          {t("clearFilters")}
        </button>
      )}
    </div>
  );
};
